/* Homepage v2 (Phase 4). Loaded with defer by the hero and Best sellers sections; each init is guarded, so twice is safe.
   1. Hero video: plays the muted loop only when motion is allowed, fades it in once it is
      really playing, and wires the pause button. Out of view it pauses; a shopper's pause sticks.
   2. Best sellers carousel: shows Previous and Next only when the row overflows, disables the
      one at each end (aria-disabled, so focus stays), and scrolls one view per press.
   Hooks: [data-hero], [data-hero-video], [data-hero-pause]; [data-home-carousel],
   [data-carousel-track], [data-carousel-prev], [data-carousel-next], [data-carousel-buttons]. */
(function () {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initHero(hero) {
    const video = hero.querySelector('[data-hero-video]');
    const button = hero.querySelector('[data-hero-pause]');
    if (!video || !button || hero.dataset.heroReady) return;
    hero.dataset.heroReady = 'true';

    let userPaused = false;
    let inView = true;

    function setButton(paused) {
      button.dataset.state = paused ? 'paused' : 'playing';
      button.setAttribute('aria-label', paused ? button.dataset.labelPlay : button.dataset.labelPause);
    }

    /* The file is chosen here, not with media on <source>, which browsers do not honour for
       video: phones get the 720p file when the section offers one. Set once, before the first play. */
    function chooseSource() {
      if (video.getAttribute('src')) return;
      const small = window.matchMedia('(max-width: 749px)').matches;
      const src = (small && video.dataset.srcSd) || video.dataset.srcHd;
      if (src) video.setAttribute('src', src);
    }

    function play() {
      if (reducedMotion.matches || userPaused || !inView) return;
      chooseSource();
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => {
          /* Autoplay refused (data saver, low power): the photo stays. */
          button.hidden = true;
        });
      }
    }

    video.addEventListener('playing', () => {
      hero.classList.add('is-playing');
      button.hidden = false;
      setButton(false);
    });

    video.addEventListener('pause', () => {
      if (userPaused) setButton(true);
    });

    button.addEventListener('click', () => {
      if (video.paused) {
        userPaused = false;
        setButton(false);
        play();
      } else {
        userPaused = true;
        video.pause();
        setButton(true);
      }
    });

    function onMotionChange() {
      if (reducedMotion.matches) {
        video.pause();
        hero.classList.remove('is-playing');
        button.hidden = true;
      } else {
        play();
      }
    }

    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', onMotionChange);
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        inView = entries[0].isIntersecting;
        if (inView) play();
        else if (!video.paused) video.pause();
      }).observe(hero);
    }

    play();
  }

  function initCarousel(root) {
    const track = root.querySelector('[data-carousel-track]');
    const buttons = root.querySelector('[data-carousel-buttons]');
    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    if (!track || !buttons || !prev || !next || root.dataset.carouselReady) return;
    root.dataset.carouselReady = 'true';

    let frame = 0;

    function update() {
      frame = 0;
      const max = track.scrollWidth - track.clientWidth;
      const overflows = max > 1;
      buttons.hidden = !overflows;
      if (!overflows) return;
      prev.setAttribute('aria-disabled', String(track.scrollLeft <= 1));
      next.setAttribute('aria-disabled', String(track.scrollLeft >= max - 1));
    }

    function schedule() {
      if (!frame) frame = requestAnimationFrame(update);
    }

    function step(direction) {
      const button = direction < 0 ? prev : next;
      if (button.getAttribute('aria-disabled') === 'true') return;
      track.scrollBy({
        left: direction * track.clientWidth,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    prev.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));
    track.addEventListener('scroll', schedule, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(track);
    else window.addEventListener('resize', schedule, { passive: true });
    update();
  }

  function init(scope) {
    scope.querySelectorAll('[data-hero]').forEach(initHero);
    scope.querySelectorAll('[data-home-carousel]').forEach(initCarousel);
  }

  init(document);

  /* Theme editor: sections re-render in place. */
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
